/**
 * Asset Picker Modal
 *
 * A reusable modal for selecting assets from the tenant's asset library.
 * Used in WorkflowPresentationEditor and other components that need to select images.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { assetService, Asset, AssetCategory } from '../../services/assetService';
import { prepareImageForUpload, formatFileSize } from '../../utils/imageUtils';


interface AssetPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (asset: Asset) => void;
  tenantId: string;
  authToken: string;
  title?: string;
  categoryFilter?: AssetCategory;
  allowUpload?: boolean;
}

export const AssetPicker: React.FC<AssetPickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  tenantId,
  authToken,
  title = 'Select Asset',
  categoryFilter,
  allowUpload = true
}) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState<AssetCategory | ''>(categoryFilter || '');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [uploading, setUploading] = useState(false);

  // Initialize auth token
  useEffect(() => {
    assetService.setAccessToken(authToken);
  }, [authToken]);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await assetService.listAssets(tenantId, {
        category: category || undefined,
        search: searchQuery || undefined,
      });
      setAssets(response.assets || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, category, searchQuery]);

  // Fetch assets when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAssets();
      setSelectedAsset(null);
    }
  }, [isOpen, fetchAssets]);

  const handleAssetClick = (asset: Asset) => {
    setSelectedAsset(asset);
  };

  const handleConfirm = () => {
    if (selectedAsset) {
      onSelect(selectedAsset);
      onClose();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);

    try {
      const file = files[0];
      const uploadCategory = (category as AssetCategory) || categoryFilter || 'thumbnails';

      // Validate and process the image (resize if needed)
      const processed = await prepareImageForUpload(file, uploadCategory);

      console.log(`📸 Processed ${file.name}: ${formatFileSize(file.size)} → ${formatFileSize(processed.size)} (${processed.width}x${processed.height})`);

      const response = await assetService.uploadAsset(tenantId, {
        dataUrl: processed.dataUrl,
        filename: file.name,
        displayName: file.name.replace(/\.[^/.]+$/, ''),
        category: uploadCategory,
      });

      // Select the newly uploaded asset
      setSelectedAsset(response.asset);
      fetchAssets();
    } catch (err: any) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  // formatFileSize is now imported from imageUtils

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="w-full sm:max-w-5xl bg-white rounded-t-2xl sm:rounded-xl shadow-xl flex flex-col overflow-hidden h-[92dvh] sm:h-auto sm:max-h-[88dvh]"
           onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0 pt-[max(1rem,env(safe-area-inset-top))] sm:pt-4">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button type="button" onClick={onClose}
            className="touch-target flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-fast text-lg">×</button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <input type="text" placeholder="Search assets…" value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast placeholder:text-gray-400" />
          {!categoryFilter && (
            <select aria-label="Filter by category" value={category}
              onChange={e => setCategory(e.target.value as AssetCategory | '')}
              className="px-3 py-2 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast select-input">
              <option value="">All Categories</option>
              <option value="thumbnails">Thumbnails</option>
              <option value="references">References</option>
              <option value="icons">Icons</option>
              <option value="general">General</option>
            </select>
          )}
          {allowUpload && (
            <label className="touch-target px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 text-label font-semibold rounded-md hover:bg-blue-100 transition-colors duration-fast cursor-pointer flex items-center justify-center">
              {uploading ? 'Uploading…' : 'Upload'}
              <input type="file" onChange={handleFileUpload}
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                disabled={uploading} aria-label="Upload asset file" className="hidden" />
            </label>
          )}
        </div>

        {error && <div className="mx-5 mt-3 px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label flex-shrink-0">{error}</div>}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0 scrollbar-touch">
          {loading ? (
            <div className="py-12 text-center text-body text-gray-400">Loading assets…</div>
          ) : assets.length === 0 ? (
            <div className="py-12 text-center">
              <div className="text-4xl mb-3">📁</div>
              <p className="text-body text-gray-500 mb-1">No assets found</p>
              {allowUpload && <p className="text-label text-gray-400">Upload an image to get started</p>}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {assets.map(asset => {
                const isSel = selectedAsset?.assetId === asset.assetId;
                return (
                  <button key={asset.assetId} type="button" onClick={() => handleAssetClick(asset)}
                    className={`text-left rounded-lg border-2 overflow-hidden transition-all duration-base bg-white touch-target ${
                      isSel ? 'border-blue-500 shadow-focus' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}>
                    <div className="aspect-square bg-gray-100 overflow-hidden">
                      <img src={asset.url} alt={asset.displayName} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="text-meta text-gray-900 font-medium truncate">{asset.displayName}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected preview */}
        {selectedAsset && (
          <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-100 bg-surface-subtle flex-shrink-0">
            <img src={selectedAsset.url} alt={selectedAsset.displayName}
              className="w-12 h-12 rounded-md object-cover border border-gray-200" />
            <div>
              <div className="text-label font-semibold text-gray-900">{selectedAsset.displayName}</div>
              <div className="text-meta text-gray-500 capitalize">{selectedAsset.category} • {formatFileSize(selectedAsset.size)}</div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 border-t border-gray-200 flex-shrink-0 safe-b">
          <button type="button" onClick={onClose}
            className="touch-target px-4 py-2.5 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast">
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} disabled={!selectedAsset}
            className="flex-1 touch-target py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed">
            Select
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetPicker;

